import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { useQuery } from "@tanstack/react-query";
import { fetchHelpVideos } from "@/api/help";
import { CoraAvatar } from "@/components/help/CoraAvatar";
import { isCoraEnabled } from "@/config/publicEnv";
import { CORA_UI } from "@/constants/cora";
import { HELP_RAIL_FALLBACK_LABEL, HELP_TOPICS, HELP_WATCH } from "@/constants/help";
import { useCoraSettings } from "@/hooks/useCoraSettings";
import type { HelpPanelView, HelpSection } from "@/interfaces/help";

interface HelpPanelRailProps {
  view: HelpPanelView;
  screenLabel: string | null;
  onSelectScreen(): void;
  onSelectSection(section: HelpSection): void;
  onSelectChat(): void;
  onTalkToHuman(): void;
  onSelectWatch(): void;
}

const RAIL_BUTTON_CLASS =
  "rounded-md px-2.5 py-2 text-left font-lato text-[13px] text-graphite transition-colors hover:bg-light-dust hover:text-rodeo-black";
const RAIL_BUTTON_SELECTED_CLASS = "bg-light-dust font-bold text-rodeo-black";

/**
 * The panel's left rail: a `.hc-rail` nav landmark. When isCoraEnabled() is true it opens with the
 * Ask Cora card (selected while the chat, or an article opened from it, is shown) and the "Or talk
 * to a human" link; then "This screen", then "Watch" (the walkthrough-video listing, omitted while
 * there are none), then "Topics" (one item per HELP_TOPICS entry). An article opened from Watch or
 * a topic keeps that item highlighted. Hidden below 900px per the design of record's
 * `@media (max-width: 900px) { .hc-rail { display: none } }` — declared here, not in a shared
 * constant, since only this file needs it; HelpScreenView carries the narrow-screen topic chips
 * and CoraChatView its own "Or talk to a human" link instead.
 */
export function HelpPanelRail({
  view,
  screenLabel,
  onSelectScreen,
  onSelectSection,
  onSelectChat,
  onTalkToHuman,
  onSelectWatch,
}: HelpPanelRailProps) {
  const coraSettings = useCoraSettings();
  const isScreenSelected = view.kind === "screen" || view.kind === "search";
  const isChatSelected = view.kind === "chat" || (view.kind === "article" && !!view.fromChat);
  const isWatchSelected = view.kind === "watch" || (view.kind === "article" && !!view.fromWatch);
  const coraEnabled = isCoraEnabled();

  const { data: videos } = useQuery({
    queryKey: ["help", "videos"],
    queryFn: fetchHelpVideos,
  });
  const videoCount = videos?.length ?? 0;
  const totalMinutes = Math.max(
    1,
    Math.round((videos ?? []).reduce((sum, a) => sum + (a.videoSeconds ?? 0), 0) / 60),
  );

  function isSectionSelected(section: HelpSection) {
    return (
      (view.kind === "section" && view.section === section) ||
      (view.kind === "article" && view.fromSection === section)
    );
  }

  return (
    <nav
      aria-label="Help topics"
      className="w-[244px] flex-none overflow-y-auto border-card-border border-r bg-clear-dust p-3 [overscroll-behavior:contain] max-[900px]:hidden"
    >
      {coraEnabled && (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={onSelectChat}
            aria-current={isChatSelected ? "true" : undefined}
            className={`flex w-full cursor-pointer items-center gap-2.5 rounded-[10px] border px-3 py-[11px] text-left transition-colors hover:border-dark-dust focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2 ${
              isChatSelected ? "border-dark-dust bg-light-dust" : "border-smoke bg-white"
            }`}
          >
            <CoraAvatar size={38} />
            <span className="flex min-w-0 flex-col">
              <span className="font-bold font-lato text-[13px] text-rodeo-black">
                {CORA_UI.ASK_CORA}
              </span>
              <span className="font-lato text-[12px] text-graphite">{coraSettings.tagline}</span>
            </span>
          </button>
          <button
            type="button"
            onClick={onTalkToHuman}
            className="flex cursor-pointer items-center gap-1.5 self-start rounded border-0 bg-transparent px-3 py-1.5 font-bold font-lato text-[13px] text-dust-ink transition-colors hover:text-rodeo-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2"
          >
            <SupportAgentIcon aria-hidden="true" sx={{ fontSize: 17 }} />
            {CORA_UI.TALK_TO_HUMAN}
          </button>
        </div>
      )}

      <p
        className={`m-0 mb-2.5 px-2.5 font-crimson font-normal text-[20px] text-rodeo-black ${
          coraEnabled ? "mt-3 border-smoke border-t pt-[18px]" : ""
        }`}
      >
        This screen
      </p>
      <div className="flex flex-col gap-px">
        <button
          type="button"
          onClick={onSelectScreen}
          aria-current={isScreenSelected ? "true" : undefined}
          className={`${RAIL_BUTTON_CLASS} ${isScreenSelected ? RAIL_BUTTON_SELECTED_CLASS : ""}`}
        >
          {screenLabel ?? HELP_RAIL_FALLBACK_LABEL}
        </button>
      </div>

      {videoCount > 0 && (
        <>
          <p className="mx-0 mt-[22px] mb-2.5 border-smoke border-t px-2.5 pt-[18px] font-crimson font-normal text-[20px] text-rodeo-black">
            {HELP_WATCH.HEADING}
          </p>
          <div className="flex flex-col gap-px">
            <button
              type="button"
              onClick={onSelectWatch}
              aria-current={isWatchSelected ? "true" : undefined}
              className={`${RAIL_BUTTON_CLASS} ${isWatchSelected ? RAIL_BUTTON_SELECTED_CLASS : ""}`}
            >
              <span className="block">
                {videoCount}{" "}
                {videoCount === 1 ? HELP_WATCH.WALKTHROUGH_SINGULAR : HELP_WATCH.WALKTHROUGHS}
              </span>
              <span className="block font-normal text-[12px] text-graphite">
                {totalMinutes} {HELP_WATCH.TOTAL_SUFFIX}
              </span>
            </button>
          </div>
        </>
      )}

      <p className="mx-0 mt-[22px] mb-2.5 border-smoke border-t px-2.5 pt-[18px] font-crimson font-normal text-[20px] text-rodeo-black">
        Topics
      </p>
      <div className="flex flex-col gap-px">
        {HELP_TOPICS.map((t) => {
          const isSelected = isSectionSelected(t.section);
          return (
            <button
              key={t.section}
              type="button"
              onClick={() => onSelectSection(t.section)}
              aria-current={isSelected ? "true" : undefined}
              className={`${RAIL_BUTTON_CLASS} ${isSelected ? RAIL_BUTTON_SELECTED_CLASS : ""}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default HelpPanelRail;
